import { olympusResourceToNewResource } from './olympus.scopes.js';
/**
 * Authorization provider for Olympus legacy auth system
 * Adapts Olympus session data (user + brandSession) to Authorizer format
 */
export class OlympusAuthorizationProvider {
    name = 'olympus';
    user;
    brandSession;
    permissionTree;
    constructor(user, brandSession) {
        this.user = user;
        this.brandSession = brandSession;
    }
    async initialize() {
        if (!this.brandSession.brandId && this.brandSession.role !== 'pb:super') {
            throw new Error('Brand ID is required for non-super admin roles');
        }
        this.permissionTree = this.buildPermissionTree();
    }
    getPermissionTree() {
        if (!this.permissionTree) {
            throw new Error('Provider not initialized');
        }
        return this.permissionTree;
    }
    getUserContext() {
        return {
            userId: this.user.id,
            email: this.user.email,
            phone: this.user.phone,
            brandId: this.brandSession.brandId,
            locationIds: this.brandSession.locationIds ?? [],
            brandRoles: [this.mapOlympusRole(this.brandSession.role)],
        };
    }
    getAuthProvider() {
        return 'olympus';
    }
    buildPermissionTree() {
        const { role, scopes, locationIds } = this.brandSession;
        const mappedRole = this.mapOlympusRole(role);
        const rolesByLocation = {};
        if (locationIds && locationIds.length > 0) {
            for (const locId of locationIds) {
                rolesByLocation[locId] = [mappedRole];
            }
        }
        const { permissionsByRole, permissionsById } = this.buildPermissionsFromScopes(mappedRole, scopes);
        return {
            rolesByLocation,
            permissionsByRole,
            permissionsById,
        };
    }
    buildPermissionsFromScopes(role, scopes) {
        const permissionIds = [];
        const permissionsById = {};
        let permIdCounter = 0;
        for (const scope of scopes) {
            const parts = scope.split(':');
            let mappedResource = null;
            let mappedOperation = null;
            if (parts.length === 1) {
                // resource → resource:*
                mappedResource = this.mapResource(parts[0]);
                mappedOperation = '*';
            }
            else if (parts.length === 2) {
                const [resource, secondPart] = parts;
                const baseResource = this.mapResource(resource);
                const operation = this.mapOperation(secondPart);
                if (baseResource && operation) {
                    // resource:operation (e.g., orders:read)
                    mappedResource = baseResource;
                    mappedOperation = operation;
                }
                // note: we don't handle resource:sub-resource patterns here
                // as they should be rare and likely indicate invalid scopes
            }
            else if (parts.length === 3) {
                const [resource, middle, last] = parts;
                const baseResource = this.mapResource(resource);
                if (!baseResource) {
                    continue;
                }
                // pattern: resource:operation:sub-resource first
                // prioritized because sub-resource names might also map to operations
                // (e.g., orders:adjust:charge where both 'adjust' and 'charge' map to 'manage')
                const fullResourceFromLast = this.buildResourceName(baseResource, last);
                const middleAsOperation = this.mapOperation(middle);
                if (fullResourceFromLast && middleAsOperation) {
                    // pattern: resource:operation:sub-resource
                    // (e.g., orders:adjust:charge -> order.charge:manage)
                    mappedResource = fullResourceFromLast;
                    mappedOperation = middleAsOperation;
                }
                else {
                    // pattern: resource:sub-resource:operation
                    const fullResourceFromMiddle = this.buildResourceName(baseResource, middle);
                    const lastAsOperation = this.mapOperation(last);
                    if (fullResourceFromMiddle && lastAsOperation) {
                        // pattern: resource:sub-resource:operation
                        // (e.g., delivery:settings:read -> delivery.settings:read)
                        mappedResource = fullResourceFromMiddle;
                        mappedOperation = lastAsOperation;
                    }
                }
            }
            if (mappedResource && mappedOperation) {
                const permId = `perm_${role}_${permIdCounter++}`;
                permissionIds.push(permId);
                permissionsById[permId] = {
                    resource: mappedResource,
                    operation: mappedOperation,
                };
            }
        }
        return {
            permissionsByRole: { [role]: permissionIds },
            permissionsById,
        };
    }
    /**
     * Builds a resource name with sub-resource in dot notation
     * E.g., 'customer' + 'subscriptions' → 'customer.subscriptions'
     */
    buildResourceName(baseResource, subResourcePart) {
        // convert sub-resource part to singular/kebab-case if needed
        const subResourceMap = {
            subscriptions: 'subscriptions',
            theme: 'theme',
            settings: 'settings',
            balance: 'balance',
            kds: 'kds',
            location: 'location',
            scan: 'scan',
            'special-hours': 'special-hours',
            charge: 'charge',
            refund: 'refund',
            status: 'status',
            submissions: 'submission',
            acceptances: 'acceptance',
            agreements: 'agreement',
            applications: 'applications',
            admin: 'admin',
            employee: 'employee',
            shifts: 'shifts',
            events: 'events',
            shiftEvents: 'shiftEvents',
            manager: 'manager',
            password: 'password',
            notifications: 'notifications',
            staff: 'staff',
            sms: 'sms',
            email: 'email',
            'email-sections': 'email-section',
            'sms-sections': 'sms-section',
            campaigns: 'campaign',
            blocks: 'block',
            redirects: 'redirect',
            pages: 'page',
            otter: 'otter',
            likes: 'likes',
            availability: 'availability',
            jobs: 'jobs',
            fulfillment: 'fulfillment',
        };
        const mappedSubResource = subResourceMap[subResourcePart];
        if (!mappedSubResource) {
            return null;
        }
        // full resource name with dot notation
        const fullResourceName = `${baseResource}.${mappedSubResource}`;
        return fullResourceName;
    }
    mapOlympusRole(olympusRole) {
        const roleMap = {
            'pb:super': 'pb:super', // will need to update to whatever we decide new super admin role is
            'brand:admin': 'brand:admin',
            'brand:kitchen': 'brand:kitchen',
            'olo:customer': 'olo:customer',
            'olo:guest': 'olo:guest',
        };
        const role = roleMap[olympusRole];
        if (!role) {
            throw new Error(`Invalid Olympus role: ${olympusRole}`);
        }
        return role;
    }
    mapResource(scopeResource) {
        const mapped = olympusResourceToNewResource[scopeResource];
        return mapped ?? null;
    }
    mapOperation(scopeOperation) {
        const mapping = {
            read: 'read',
            create: 'create',
            update: 'update',
            delete: 'delete',
            // edit operation
            edit: 'edit',
            // management operations
            manage: 'manage',
            admin: 'manage',
            // special operations that map to manage
            adjust: 'manage',
            login: 'manage',
            register: 'manage',
            reload: 'manage',
            charge: 'manage',
            reset: 'manage',
            run: 'manage',
            // wildcard
            '*': '*',
            '': '*',
        };
        return mapping[scopeOperation] ?? null;
    }
}
//# sourceMappingURL=olympus.provider.js.map